/**
 * Pure helpers for the due-date urgency surface on /portal/orders
 * (kanban 3d77f755). Kept out of the page component so they can be unit
 * tested without rendering.
 */
import type { DueUrgency, OrderRecord } from '../../types/orders';

type DueRow = Pick<OrderRecord, 'due_urgency' | 'due_desk_state'>;

/** Most urgent first; anything without a reading sorts last. */
const RANK: Record<DueUrgency, number> = { overdue: 0, due_today: 1, due_soon: 2 };

/**
 * Rank for hoisting: open at-risk rows by urgency, then the ones the desk
 * has already answered (acknowledged / saved for later) — still due, but no
 * longer shouting — then everything else.
 */
export function dueRank(order: DueRow): number {
  if (!order.due_urgency) return Number.POSITIVE_INFINITY;
  return RANK[order.due_urgency] + (order.due_desk_state ? 10 : 0);
}

export function isDue(order: DueRow): boolean {
  return !!order.due_urgency;
}

/**
 * Whether the whole row gets the red left accent: the date is today or has
 * passed AND nobody on the desk has answered the alert yet. Due-soon rows
 * and handled rows keep their badge but not the accent, so the accent means
 * "act now" and nothing else.
 */
export function dueRowAccent(order: DueRow): boolean {
  if (order.due_desk_state) return false;
  return order.due_urgency === 'overdue' || order.due_urgency === 'due_today';
}

/**
 * Hoist at-risk rows to the top (overdue, then due today, then due soon)
 * while keeping the server's order inside each band and for everything
 * else. Stable: `Array.prototype.sort` is stable in every supported browser.
 */
export function hoistDueDrafts<T extends DueRow>(orders: readonly T[]): T[] {
  return [...orders].sort((a, b) => dueRank(a) - dueRank(b));
}

export function countDue<T extends DueRow>(orders: readonly T[]): number {
  return orders.reduce((n, o) => (isDue(o) ? n + 1 : n), 0);
}
